import type { AdminUsageDashboardResponse, AdminUsageSeatRow } from '@nadoutong/contracts';
import { Search } from 'lucide-react';
import { Sparkline } from '../../charts/index.ts';
import {
  formatTokens,
  formatUsdCentsAsCny,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey, TParams } from '../../i18n/index.ts';
import { Chip } from '../../ui/Chip.tsx';
import type { ChipTone } from '../../ui/Chip.tsx';
import { Pagination } from '../../ui/Pagination.tsx';
import { Table, TBody, THead, Td, Th, Tr } from '../../ui/Table.tsx';
import { cx } from '../../ui/cx.ts';
import { formatCompactCount, uncachedInput } from './format.ts';
import { formatZonedDay } from './monthWindow.ts';

/**
 * Per-member usage table — the one place on `/usage` that is paged and
 * searched, which is why it is its own module.
 *
 * Both the search box and the pager are controlled from `AdminUsage`:
 * the search term is part of the dashboard query (the server filters
 * and pages, this table never slices locally), and offset has to reset
 * whenever any other filter moves. That coupling belongs to the page,
 * so this file stays a renderer of `dashboard.seats` plus two
 * callbacks.
 *
 * The row identity is `memberAccountId`, not the display name — a seat
 * with no name and no email still has to be addressable in a test and
 * stable across re-sorts.
 *
 * Layout: this is the widest table in the console (12 columns), so the
 * minimum widths that keep the desktop grid from collapsing live on the
 * individual `Th`s, NOT as a `min-w-*` on the table. A table-level
 * minimum would win over `Table`'s `max-md:block`, and the phone layout
 * would silently stay a 1300px-wide grid inside a scroller instead of
 * becoming per-row cards (DESIGN.md §6). `THead` is `max-md:hidden`, so
 * per-column minimums cost the card layout nothing.
 */

/** Desktop column minimums. Identity and model columns hold text; the rest are numeric. */
const COL_MEMBER = 'min-w-[240px]';
const COL_TEXT = 'min-w-[150px]';
const COL_MODEL = 'min-w-[180px]';
const COL_NUM = 'min-w-[110px]';
const COL_MONEY = 'min-w-[130px]';
/** Wide enough for the "no monthly cap" sentence, not just an amount. */
const COL_BUDGET = 'min-w-[170px]';
const COL_TREND = 'min-w-[120px]';

export function SeatUsageTable(props: {
  readonly dashboard: AdminUsageDashboardResponse;
  readonly cnyPerUsd: number | null;
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly onOffset: (offset: number) => void;
}): JSX.Element {
  const t = useT();
  const seats = props.dashboard.seats;
  const monthlyWindow = props.dashboard.monthlyWindow;
  // The server pages by offset; `Pagination` speaks 1-based pages. The
  // two `Math.max(1, ...)` floors keep a zero/absent limit from turning
  // the conversion into a division by zero.
  const page = Math.floor(seats.offset / Math.max(1, seats.limit)) + 1;
  const pageCount = Math.max(1, Math.ceil(seats.total / Math.max(1, seats.limit)));
  return (
    <article
      data-testid="usage-by-seat-card"
      className="flex flex-col rounded-lg border border-line bg-card"
    >
      <header className="flex flex-col gap-3 border-b border-line p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-heading text-ink">{t('usage.admin.seat.title')}</h3>
          <p className="text-caption text-ink-low">
            {t('usage.admin.seat.subtitle', { count: formatTokens(seats.total) })}
          </p>
          {/*
            Two different clocks meet in this table: every count and the
            cost follow the filter bar's range, while the budget column
            follows the calendar month the server closes over. The column
            groups say which is which on desktop, but `THead` is hidden in
            the phone card layout — so the month window is also stated
            here, in prose, where it survives at every width.
          */}
          <p
            data-testid="usage-seat-month-window"
            className="mt-1 max-w-prose text-caption text-ink-low"
          >
            {t('usage.admin.seat.monthly.window_note', {
              from: formatZonedDay(monthlyWindow.from, monthlyWindow.timezone),
              to: formatZonedDay(monthlyWindow.to, monthlyWindow.timezone),
              zone: monthlyWindow.timezone,
            })}
          </p>
        </div>
        {/*
          The wrapping label holds only an aria-hidden glyph, so the
          input has no accessible name without this explicit one.
        */}
        <label className="inline-flex h-10 min-w-72 items-center gap-2 rounded-lg border border-line bg-raised px-3 text-body text-ink-low">
          <Search aria-hidden="true" className="h-4 w-4" />
          <input
            value={props.search}
            onChange={(event) => props.onSearch(event.target.value)}
            placeholder={t('usage.admin.seat.search_placeholder')}
            aria-label={t('usage.admin.seat.search_label')}
            className="min-w-0 flex-1 bg-transparent text-ink placeholder:text-ink-faint focus:outline-none"
            data-testid="usage-seat-search"
          />
        </label>
      </header>
      {seats.rows.length === 0 ? (
        <p className="p-5 text-body text-ink-low" data-testid="usage-by-seat-empty">
          {t('usage.admin.seat.empty')}
        </p>
      ) : (
        // The card already draws the frame; the table drops its own.
        <Table testId="usage-by-seat-table" className="rounded-none border-0">
          <THead>
            {/*
              Scope band. Without it "Cost" (filter range) and "Monthly
              budget" (calendar month) sit side by side with nothing to
              tell a reader they are counted over different periods.
            */}
            <Tr interactive={false} data-testid="usage-seat-column-groups">
              <Th scope="colgroup" colSpan={3} className="py-2 text-caption">
                {t('usage.admin.seat.group.identity')}
              </Th>
              <Th scope="colgroup" colSpan={7} className="py-2 text-caption">
                {t('usage.admin.seat.group.range')}
              </Th>
              <Th scope="colgroup" className="py-2 text-caption">
                {t('usage.admin.seat.group.month')}
              </Th>
              <Th scope="colgroup" className="py-2 text-caption">
                {t('usage.admin.seat.group.trend')}
              </Th>
            </Tr>
            <Tr interactive={false}>
              <Th className={COL_MEMBER}>{t('usage.admin.seat.column.member')}</Th>
              <Th className={COL_TEXT}>{t('usage.admin.seat.column.team')}</Th>
              <Th className={COL_MODEL}>{t('usage.admin.seat.column.primary_model')}</Th>
              <Th num className={COL_NUM}>
                {t('usage.summary.tokens_in')}
              </Th>
              <Th num className={COL_NUM}>
                {t('usage.summary.tokens_out')}
              </Th>
              <Th num className={COL_NUM}>
                {t('usage.summary.cache_write')}
              </Th>
              <Th num className={COL_NUM}>
                {t('usage.summary.cache_read')}
              </Th>
              <Th num className={COL_NUM}>
                {t('usage.summary.requests')}
              </Th>
              <Th num className={COL_NUM}>
                {t('usage.admin.seat.column.avg_per_request')}
              </Th>
              <Th num className={COL_MONEY}>
                {t('usage.summary.cost')}
              </Th>
              <Th num className={COL_BUDGET}>
                {t('usage.admin.seat.column.monthly_cap')}
              </Th>
              <Th className={COL_TREND}>{t('usage.admin.seat.column.trend_14d')}</Th>
            </Tr>
          </THead>
          <TBody>
            {seats.rows.map((seat) => (
              <Tr
                key={seat.memberAccountId}
                data-testid={`usage-seat-row-${seat.memberAccountId}`}
                data-status={seat.status}
              >
                <Td heading>
                  <div className="flex items-center gap-3">
                    <Avatar label={displaySeatName(seat)} />
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <p
                          className={cx(
                            'truncate font-medium',
                            seat.status === 'active' ? 'text-ink' : 'text-ink-low',
                          )}
                        >
                          {displaySeatName(seat)}
                        </p>
                        <SeatStatusChip seat={seat} t={t} />
                      </div>
                      <p className="truncate font-mono text-caption text-ink-low">
                        {seat.email ?? seat.memberAccountId}
                      </p>
                    </div>
                  </div>
                </Td>
                <Td label={t('usage.admin.seat.column.team')}>
                  <span className="rounded-md bg-raised px-2 py-1 text-caption font-medium text-ink-mid">
                    {seat.department ?? t('usage.admin.department_mix.unassigned')}
                  </span>
                </Td>
                <Td label={t('usage.admin.seat.column.primary_model')}>
                  <span className="rounded-md bg-raised px-2 py-1 font-mono text-caption text-ink">
                    {seat.primaryModel ?? seat.defaultModel ?? '--'}
                  </span>
                </Td>
                <Td num label={t('usage.summary.tokens_in')}>
                  {/*
                    Same uncached caliber as the summary card.
                    `inputTokens` double-counts cache reads on the OpenAI lane.
                  */}
                  {formatCompactCount(
                    uncachedInput({
                      cacheEligibleInputTokens: seat.cacheEligibleInputTokens,
                      cacheReadTokens: seat.cacheReadTokens,
                      cacheWriteTokens: seat.cacheWriteTokens,
                    }),
                  )}
                </Td>
                <Td num label={t('usage.summary.tokens_out')}>
                  {formatCompactCount(seat.outputTokens)}
                </Td>
                <Td num label={t('usage.summary.cache_write')}>
                  {formatCompactCount(seat.cacheWriteTokens)}
                </Td>
                <Td num label={t('usage.summary.cache_read')}>
                  {formatCompactCount(seat.cacheReadTokens)}
                </Td>
                <Td num label={t('usage.summary.requests')}>
                  {formatTokens(seat.requests)}
                </Td>
                <Td num label={t('usage.admin.seat.column.avg_per_request')}>
                  {seat.averageTokensPerRequest === null
                    ? '--'
                    : formatCompactCount(seat.averageTokensPerRequest)}
                </Td>
                <Td num label={t('usage.admin.seat.column.cost_scoped')} className="text-forest">
                  {formatUsdMicrosOrCentsAsCnyMetered(
                    seat.costUsdMicros,
                    seat.costUsdCents,
                    props.cnyPerUsd,
                  )}
                </Td>
                {/*
                  The cap, and deliberately ONLY the cap. The contract
                  also carries `monthlyUsedUsdCents`,
                  `monthlyRemainingUsdCents` and `monthlyUsageRatio`,
                  but all three descend from a cost the metering layer
                  floors to whole cents when it writes the row, so every
                  sub-cent request is stored as zero and the month-to-
                  date figure understates without bound. The quota that
                  actually throttles a member runs on a separate, correct
                  micros counter — so rendering the distorted one would
                  tell an admin a member has room left at the moment the
                  gateway starts blocking them. There is no micros twin
                  for this group to fall back to. Add the consumption
                  numbers once the server sends micros (router#1656).
                */}
                <Td
                  num
                  label={t('usage.admin.seat.column.monthly_scoped')}
                  data-testid={`usage-seat-monthly-${seat.memberAccountId}`}
                  data-capped={seat.monthlyLimitUsdCents !== null && seat.monthlyLimitUsdCents > 0}
                >
                  {seat.monthlyLimitUsdCents === null || seat.monthlyLimitUsdCents <= 0 ? (
                    // A missing cap has no amount to show. `--` reads as
                    // "data missing" and a formatted 0 would read as an
                    // exhausted allowance; neither is what null means.
                    <span className="font-body text-ink-low">
                      {t('usage.admin.seat.monthly.no_limit')}
                    </span>
                  ) : (
                    formatUsdCentsAsCny(seat.monthlyLimitUsdCents, props.cnyPerUsd)
                  )}
                </Td>
                <Td stack label={t('usage.admin.seat.column.trend_14d')}>
                  <Sparkline
                    values={seat.trend14d}
                    ariaLabel={t('usage.admin.seat.column.trend_14d')}
                    testId={`usage-seat-sparkline-${seat.memberAccountId}`}
                  />
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <footer className="flex items-center justify-between gap-3 p-4 text-body text-ink-low">
        <span>
          {t('usage.admin.seat.range', {
            from: seats.total === 0 ? 0 : seats.offset + 1,
            to: Math.min(seats.offset + seats.limit, seats.total),
            total: seats.total,
          })}
        </span>
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={(next) => props.onOffset((next - 1) * seats.limit)}
          prevLabel={t('usage.pagination.previous')}
          nextLabel={t('usage.pagination.next')}
          ariaLabel={t('usage.admin.seat.pagination_aria')}
          testId="usage-seat-pagination"
        />
      </footer>
    </article>
  );
}

/**
 * Membership state, for the two states that are NOT `active`.
 *
 * The contract has carried `status` since the seat row existed and
 * neither console ever rendered it, so a member who was disabled or
 * removed has been indistinguishable from a working one — their
 * historical usage keeps them in the table forever. An active seat gets
 * no chip: it is the overwhelming majority, and a badge on every row
 * would say nothing.
 */
const STATUS_CHIP: Readonly<
  Record<
    Exclude<AdminUsageSeatRow['status'], 'active'>,
    { readonly tone: ChipTone; readonly key: I18nKey }
  >
> = {
  disabled: { tone: 'warn', key: 'usage.admin.seat.status.disabled' },
  deleted: { tone: 'neutral', key: 'usage.admin.seat.status.deleted' },
};

function SeatStatusChip(props: {
  readonly seat: Pick<AdminUsageSeatRow, 'status' | 'memberAccountId'>;
  readonly t: (key: I18nKey, params?: TParams) => string;
}): JSX.Element | null {
  if (props.seat.status === 'active') {
    return null;
  }
  const chip = STATUS_CHIP[props.seat.status];
  return (
    <Chip
      tone={chip.tone}
      testId={`usage-seat-status-${props.seat.memberAccountId}`}
      className="shrink-0"
    >
      {props.t(chip.key)}
    </Chip>
  );
}

/** Initials monogram; falls back to `M` so a nameless seat still gets a glyph. */
function Avatar({ label }: { readonly label: string }): JSX.Element {
  const initials =
    label
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'M';
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest font-mono text-caption font-medium text-on-forest">
      {initials}
    </span>
  );
}

/** Best available human label for a seat, id as the guaranteed fallback. */
function displaySeatName(
  seat: Pick<AdminUsageSeatRow, 'memberName' | 'email' | 'memberAccountId'>,
): string {
  return seat.memberName ?? seat.email ?? seat.memberAccountId;
}
